import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAdminClients, getExpiringPriorAuths, snoozePriorAuth } from '../utils/api';
import { paymentsApi, subscribe } from '../utils/paymentsStore';
import { marketingApi } from '../marketing/marketingApi';
import { CLAIM_STATUSES, STATUS_LABELS, monthLabel } from '../utils/claimMath';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, useToast, fmtMoney, fmtDate, fmtNum } from '../components/ui';
import { ChartCard, GroupedBar, HBar, SimpleTable, SERIES } from '../components/charts';

const RANGES = [
  { key: '3', label: 'Last 3 months' },
  { key: '6', label: 'Last 6 months' },
  { key: '12', label: 'Last 12 months' },
  { key: 'ytd', label: 'Year to date' },
];

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}

function bucketOf(days) {
  if (days < 0) return 'expired';
  if (days <= 7) return '7';
  if (days <= 14) return '14';
  return '30';
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showToast, toastNode] = useToast();

  const [payerFilter, setPayerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [range, setRange] = useState('12');
  const [search, setSearch] = useState('');

  const [payers, setPayers] = useState([]);
  const [claims, setClaims] = useState(null);
  const [auths, setAuths] = useState(null);
  const [clients, setClients] = useState(null);
  const [mkt, setMkt] = useState(null);
  const [mktError, setMktError] = useState(false);
  const [bucket, setBucket] = useState('all');

  const loadPayments = useCallback(async () => {
    const months = range === 'ytd' ? new Date().getMonth() + 1 : Number(range);
    const from = new Date(); from.setMonth(from.getMonth() - months + 1); from.setDate(1);
    const [p, c] = await Promise.all([
      paymentsApi.listPayers(),
      paymentsApi.listClaims({ payerId: payerFilter || undefined, status: statusFilter || undefined, from: from.toISOString().slice(0, 10) }),
    ]);
    setPayers(p); setClaims(c);
  }, [payerFilter, statusFilter, range]);

  useEffect(() => { loadPayments(); return subscribe(loadPayments); }, [loadPayments]);

  useEffect(() => {
    getExpiringPriorAuths().then(r => setAuths(r.authorizations || [])).catch(() => setAuths([]));
    getAdminClients().then(r => setClients(r.clients || [])).catch(() => setClients([]));
    marketingApi.getDashboard().then(setMkt).catch(() => setMktError(true));
  }, []);

  const months = useMemo(() => {
    const n = range === 'ytd' ? new Date().getMonth() + 1 : Number(range);
    const out = [];
    const d = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const x = new Date(d.getFullYear(), d.getMonth() - i, 1);
      out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`);
    }
    return out;
  }, [range]);

  const summary = useMemo(() => {
    if (!claims) return null;
    const byMonth = Object.fromEntries(months.map(m => [m, { billed: 0, paid: 0 }]));
    const byPayer = {};
    let billed = 0, paid = 0, pending = 0, denied = 0, appealed = 0, partial = 0;
    for (const c of claims) {
      billed += c.amountBilledCents; paid += c.amountPaidCents;
      if (c.status === 'pending') pending++;
      if (c.status === 'denied') denied++;
      if (c.status === 'appealed') appealed++;
      if (c.status === 'partial_paid') partial++;
      if (byMonth[c.serviceMonth]) { byMonth[c.serviceMonth].billed += c.amountBilledCents; byMonth[c.serviceMonth].paid += c.amountPaidCents; }
      const k = c.payerName || 'Unknown payer';
      byPayer[k] = byPayer[k] || { billed: 0, paid: 0 };
      byPayer[k].billed += c.amountBilledCents; byPayer[k].paid += c.amountPaidCents;
    }
    return { billed, paid, outstanding: billed - paid, pending, denied, appealed, partial, byMonth, byPayer, open: claims.filter(c => c.isOutstanding).length };
  }, [claims, months]);

  const attention = useMemo(() => {
    if (!claims) return [];
    const today = new Date();
    const ageDays = c => Math.round((today - new Date(`${c.dosEnd || c.dosStart}T00:00:00`)) / 86400000);
    const rank = { denied: 0, appealed: 1, partial_paid: 2, pending: 3 };
    return claims
      .filter(c => c.status !== 'paid' && (c.status !== 'pending' || ageDays(c) > 45))
      .map(c => ({ ...c, ageDays: ageDays(c) }))
      .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || b.ageDays - a.ageDays)
      .slice(0, 6);
  }, [claims]);

  const authRows = useMemo(() => {
    if (!auths) return [];
    const withBucket = auths.map(a => ({ ...a, bucket: bucketOf(a.daysUntilExpiry) }));
    withBucket.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
    return bucket === 'all' ? withBucket : withBucket.filter(a => a.bucket === bucket);
  }, [auths, bucket]);
  const authCounts = useMemo(() => {
    const c = { expired: 0, 7: 0, 14: 0, 30: 0 };
    (auths || []).forEach(a => { c[bucketOf(a.daysUntilExpiry)]++; });
    return c;
  }, [auths]);

  const clientCounts = useMemo(() => ({
    active: (clients || []).filter(c => c.serviceStatus !== 'service-ended').length,
    ended: (clients || []).filter(c => c.serviceStatus === 'service-ended').length,
  }), [clients]);

  const onSnooze = async (a, days) => {
    if (!days) return;
    try {
      await snoozePriorAuth(a.clientId, a.authId, Number(days));
      setAuths(prev => prev.filter(x => x.authId !== a.authId));
      showToast(`Snoozed ${a.clientName} for ${days} days`);
    } catch (e) { showToast(e.message || 'Snooze failed', 'error'); }
  };

  const onSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    if (/^[A-Za-z]?\d{4,}$/.test(q)) navigate(`/payments?q=${encodeURIComponent(q)}`);
    else navigate(`/clients?q=${encodeURIComponent(q)}`);
  };

  const monthCats = months.map(monthLabel);
  const payerRows = summary ? Object.entries(summary.byPayer).sort((a, b) => b[1].billed - a[1].billed).map(([label, v]) => ({ label, values: [v.billed, v.paid] })) : [];
  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const noClaims = claims && claims.length === 0;

  return (
    <main className="main-content ops-main">
      <div className="ops-page">
        <PageHeader
          title={`${greeting()}, ${user?.firstName || 'there'}`}
          subtitle={today}
          actions={
            <>
              <form className="ops-search" onSubmit={onSearch} role="search">
                <Icon name="search" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search client, claim #, auth #" aria-label="Search" />
              </form>
              <Link to="/payments?new=1" className="ops-btn ops-btn--primary"><Icon name="plus" /> New claim</Link>

            </>
          }
        />

        <div className="ops-filters" aria-label="Dashboard filters">
          <label className="ops-field--inline">Payer
            <select className="ops-select" value={payerFilter} onChange={e => setPayerFilter(e.target.value)}>
              <option value="">All payers</option>
              {payers.map(p => <option key={p.payerId} value={p.payerId}>{p.name}</option>)}
            </select>
          </label>
          <label className="ops-field--inline">Status
            <select className="ops-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {CLAIM_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </label>
          <label className="ops-field--inline">Range
            <select className="ops-select" value={range} onChange={e => setRange(e.target.value)}>
              {RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </label>
          <span className="ops-filters__note">Filters apply to the payment figures and charts below.</span>
        </div>

        {noClaims && (
          <div className="ops-info">
            No claims yet. <Link to="/payments/import" className="ops-link">Import your Payment Log</Link>, <Link to="/payments?new=1" className="ops-link">add a claim</Link>, or <Link to="/settings" className="ops-link">load sample data</Link> to see the dashboard populated.
          </div>
        )}

        <div className="ops-kpis">
          <StatCard label="Total billed" value={summary ? fmtMoney(summary.billed) : '…'} sub={claims ? `${claims.length} claims` : ''} to="/payments" />
          <StatCard label="Total paid" value={summary ? fmtMoney(summary.paid) : '…'} sub={claims ? `${claims.filter(c => c.status === 'paid').length} paid` : ''} to="/payments?status=paid" />
          <StatCard label="Outstanding" value={summary ? fmtMoney(summary.outstanding) : '…'} sub={summary ? `${summary.open} open` : ''} tone={summary && summary.outstanding > 0 ? 'amber' : undefined} to="/payments?open=1" />
          <StatCard label="Pending / denied" value={summary ? fmtNum(summary.pending + summary.denied) : '…'} sub={summary ? `${summary.pending} pending, ${summary.denied} denied` : ''} tone={summary && summary.denied > 0 ? 'red' : undefined} to="/payments?status=denied" />
          <StatCard label="Auths expiring (30d)" value={auths ? fmtNum(auths.length) : '…'} sub={auths ? `${authCounts.expired} expired, ${authCounts[7]} due in 7d` : ''} tone={auths && auths.length ? 'amber' : undefined} to="/authorizations?tab=expiring" />
          <StatCard label="Active clients" value={clients ? fmtNum(clientCounts.active) : '…'} sub={clients ? `${clientCounts.ended} service ended` : ''} to="/clients" />
        </div>

        <div className="ops-grid-2">
          <Panel title="Authorizations expiring" count={auths ? auths.length : ''} flush
            action={<Link to="/authorizations?tab=expiring" className="ops-link">View all</Link>}>
            <div className="ops-buckets ops-panel__strip">
              {[['all', 'All'], ['expired', 'Expired'], ['7', '7 days'], ['14', '14 days'], ['30', '30 days']].map(([k, l]) => (
                <button key={k} type="button" className={`ops-bucket${bucket === k ? ' is-active' : ''}${k === 'expired' ? ' ops-bucket--red' : k === '7' ? ' ops-bucket--amber' : ''}`} onClick={() => setBucket(k)}>
                  {l} <b>{k === 'all' ? (auths ? auths.length : 0) : authCounts[k]}</b>
                </button>
              ))}
            </div>
            {!auths ? <Skeleton rows={4} /> : authRows.length === 0 ? <Empty title="Nothing expiring">No authorizations {bucket === 'all' ? 'end within 30 days' : 'in this bucket'}.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table ops-table--dash">
                  <thead><tr><th>Client</th><th>Auth #</th><th>Ends</th><th className="num">Units</th><th className="actions">Action</th></tr></thead>
                  <tbody>
                    {authRows.slice(0, 6).map(a => (
                      <tr key={a.authId}>
                        <td className="ops-cell--grow">
                          <Link to={`/clients/${encodeURIComponent(a.clientId)}/authorizations`} className="ops-cell-main ops-truncate" title={a.clientName}>{a.clientName}</Link>
                          <div className="ops-cell-sub ops-truncate" title={a.payer || ''}>{a.payer || ''}</div>
                        </td>
                        <td className="mono nowrap">{a.authNumber || ''}</td>
                        <td className="nowrap">
                          <div className="ops-cell-main">{fmtDate(a.endDate)}</div>
                          <div className={`ops-cell-sub${a.daysUntilExpiry < 0 ? ' neg' : ''}`}>{a.daysUntilExpiry < 0 ? `${Math.abs(a.daysUntilExpiry)} days ago` : `in ${a.daysUntilExpiry} days`}</div>
                        </td>
                        <td className="num">{a.unitsAuthorized ? fmtNum(a.unitsAuthorized) : ''}</td>
                        <td className="actions">
                          <div className="ops-actions">
                            <Link to={`/authorizations?auth=${encodeURIComponent(a.authId)}`} className="ops-btn ops-btn--sm">Open</Link>
                            <select className="ops-select ops-select--sm" defaultValue="" onChange={e => onSnooze(a, e.target.value)} aria-label="Snooze">
                              <option value="">Snooze</option><option value="3">3 days</option><option value="7">7 days</option><option value="14">14 days</option>
                            </select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Payments needing attention" count={attention.length} flush
            action={<Link to="/payments?open=1" className="ops-link">View all</Link>}>
            <div className="ops-stat-row ops-panel__strip">
              <div className="ops-stat"><b className="neg">{summary ? summary.denied : 0}</b><span>Denied</span></div>
              <div className="ops-stat"><b>{summary ? summary.appealed : 0}</b><span>Appealed</span></div>
              <div className="ops-stat"><b>{summary ? summary.partial : 0}</b><span>Partial</span></div>
              <div className="ops-stat"><b>{attention.filter(c => c.status === 'pending').length}</b><span>Pending over 45d</span></div>
            </div>
            {!claims ? <Skeleton rows={4} /> : attention.length === 0 ? <Empty title="All clear">No denied, appealed or stale claims.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table ops-table--dash">
                  <thead><tr><th>Patient</th><th>Claim #</th><th className="num">Billed</th><th>Status</th><th className="num">Age</th></tr></thead>
                  <tbody>
                    {attention.map(c => (
                      <tr key={c.claimId} className="is-clickable" onClick={() => navigate(`/payments?claim=${encodeURIComponent(c.claimId)}`)}>
                        <td className="ops-cell--grow"><div className="ops-cell-main ops-truncate" title={c.patientName}>{c.patientName}</div><div className="ops-cell-sub ops-truncate">{c.payerName} · {fmtDate(c.dosStart)}</div></td>
                        <td className="mono nowrap">{c.claimNumber || <span className="muted">None</span>}</td>
                        <td className="num">{fmtMoney(c.amountBilledCents)}</td>
                        <td><StatusPill status={c.status} /></td>
                        <td className="num nowrap">{c.ageDays} days</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>

        <div className="ops-grid-2">
          <ChartCard title="Monthly billed vs paid" subtitle={`By service month, ${RANGES.find(r => r.key === range)?.label.toLowerCase()}`}
            table={<SimpleTable columns={[{ key: 'month', label: 'Month' }, { key: 'billed', label: 'Billed', num: true, render: fmtMoney }, { key: 'paid', label: 'Paid', num: true, render: fmtMoney }, { key: 'outstanding', label: 'Outstanding', num: true, render: fmtMoney }]}
              rows={months.map(m => ({ month: monthLabel(m), billed: summary?.byMonth[m].billed || 0, paid: summary?.byMonth[m].paid || 0, outstanding: (summary?.byMonth[m].billed || 0) - (summary?.byMonth[m].paid || 0) }))} />}>
            <GroupedBar categories={monthCats} format={v => `$${fmtNum(v / 100)}`}
              series={[{ name: 'Billed', values: months.map(m => summary?.byMonth[m].billed || 0) }, { name: 'Paid', values: months.map(m => summary?.byMonth[m].paid || 0) }]} />
          </ChartCard>
          <ChartCard title="Payments by insurance company" subtitle="Sorted by amount billed"
            table={<SimpleTable columns={[{ key: 'label', label: 'Payer' }, { key: 'billed', label: 'Billed', num: true, render: fmtMoney }, { key: 'paid', label: 'Paid', num: true, render: fmtMoney }]}
              rows={payerRows.map(r => ({ label: r.label, billed: r.values[0], paid: r.values[1] }))} />}>
            <HBar rows={payerRows} series={['Billed', 'Paid']} colors={SERIES} format={v => `$${fmtNum(v / 100)}`} />
          </ChartCard>
        </div>

        <div className="ops-grid-2">
          <Panel title="Marketing today" action={<Link to="/marketing" className="ops-link">Go to marketing</Link>}>
            {mktError ? <Empty title="Marketing data unavailable">The marketing API did not answer. In this prototype that usually means the token was not accepted by the other API.</Empty>
              : !mkt ? <Skeleton rows={3} /> : (
              <div className="ops-stat-row ops-stat-row--wrap">
                <div className="ops-stat"><b className={mkt.posted_today ? '' : 'neg'}>{mkt.posted_today ? 'Done' : 'Not yet'}</b><span>Today's post</span></div>
                <div className="ops-stat"><b className={mkt.open_escalations ? 'neg' : ''}>{mkt.open_escalations ?? 0}</b><span><Link to="/marketing/escalations" className="ops-link">Open escalations</Link></span></div>
                <div className="ops-stat"><b>{mkt.new_leads ?? 0}</b><span><Link to="/marketing/leads" className="ops-link">New leads</Link></span></div>
                <div className="ops-stat"><b>{mkt.pending_comments ?? 0}</b><span><Link to="/marketing/comments" className="ops-link">Pending comments</Link></span></div>
              </div>
            )}
          </Panel>
          <Panel title="Recent activity" flush action={<Link to="/marketing/audit" className="ops-link">Full log</Link>}>
            {!mkt || !mkt.recent_audit?.length ? <Empty>No recent activity.</Empty> : (
              <ul className="ops-list">
                {mkt.recent_audit.slice(0, 5).map((a, i) => (
                  <li key={i}>
                    <div className="ops-list__main">
                      <div className="ops-cell-main">{a.action || a.event || a.type || 'Event'}</div>
                      <div className="ops-cell-sub">{a.details || a.summary || ''}</div>
                    </div>
                    <span className="ops-cell-sub">{a.timestamp ? new Date(a.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
      {toastNode}
    </main>
  );
}
